import { useCallback, useEffect, useRef } from 'react'
import { APP_TYPE } from 'shared/config'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useEventConversationUnread } from 'entities/Conversation/events/EventConversationUnread'
import { Inbox } from 'entities/Inbox/model/Inbox'

const FLASH_INTERVAL_MS = 2000
const FALLBACK_CONTACT_NAME = 'Somebody'

export const useTabTitleFlash = () => {
  const { inboxesStore, usersStore } = useEntitiesContext()

  const originalTitleRef = useRef(document.title)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopFlashing = useCallback(() => {
    if (!intervalRef.current) return
    clearInterval(intervalRef.current)
    intervalRef.current = null
    document.title = originalTitleRef.current
  }, [])

  useEffect(() => {
    window.addEventListener('focus', stopFlashing)
    return () => {
      window.removeEventListener('focus', stopFlashing)
      stopFlashing()
    }
  }, [stopFlashing])

  useEventConversationUnread((response) => {
    if (APP_TYPE === 'vue' || document.hasFocus()) return

    const { conversation, inbox_id } = response
    const inbox = inboxesStore.getItem(inbox_id)

    if (!inbox || inbox.isMuted || usersStore.isAwayStatus) return

    if (inbox instanceof Inbox) {
      inbox.handleUpdateUnreadCount(response)
    }

    const { unread_count, unread_count_prev } = conversation
    const isIncrease =
      (unread_count && !unread_count_prev) ||
      (unread_count && unread_count_prev && unread_count > unread_count_prev)

    if (!isIncrease) {
      if (!unread_count) stopFlashing()
      return
    }

    const { contact } = conversation
    const name = contact?.full_name || contact?.formatted_number || FALLBACK_CONTACT_NAME
    const flashTitle = `(${inboxesStore.unreadMessagesCount}) ${name} says...`

    if (intervalRef.current) clearInterval(intervalRef.current)

    document.title = flashTitle
    intervalRef.current = setInterval(() => {
      document.title =
        document.title === originalTitleRef.current ? flashTitle : originalTitleRef.current
    }, FLASH_INTERVAL_MS)
  }, [])
}
